import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { AtnaAssistant } from '../components/atna/AtnaAssistant';
import { ATNA_CONFIG } from '../components/atna/atnaConfig';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
// FR lexicon for the Brossard / Étude de cas page. EN untouched (rendered as-is).
const FR: Record<string, string> = {
  '88,266': '88\u00A0266',
  'Verified Identities': 'Identités vérifiées',
  '78%': '78\u00A0%',
  'Population Coverage': 'Couverture de la population',
  'Since 2017': 'Depuis 2017',
  'In Production': 'En production',
  Sensors: 'Capteurs',
  Dashboards: 'Tableaux de bord',
  'Data Collection': 'Collecte de données',
  'Pilot Projects': 'Projets pilotes',
  Reports: 'Rapports',
  Identity: 'Identité',
  Communications: 'Communications',
  Services: 'Services',
  'Resident Adoption': 'Adoption par les résidents',
  'Verified Intelligence': 'Intelligence vérifiée',
  'Intelligence (ATNA)': 'Intelligence (ATNA)'
};
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const METRICS = [{
  value: '88,266',
  label: 'Verified Identities'
}, {
  value: '78%',
  label: 'Population Coverage'
}, {
  value: 'Since 2017',
  label: 'In Production'
}];
const FOUNDATION_FLOW = ['Identity', 'Communications', 'Services', 'Intelligence (ATNA)'];
const COMPARISON = {
  traditional: ['Sensors', 'Dashboards', 'Data Collection', 'Pilot Projects', 'Reports'],
  bciti: ['Identity', 'Communications', 'Services', 'Resident Adoption', 'Verified Intelligence']
};
function isHighlight(step: string) {
  return step.includes('ATNA');
}
function HorizontalFlow({
  steps,
  t
}: {
  steps: string[];
  t: (en: string) => string;
}) {
  return <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-4" data-id="element-1185">
      {steps.map((step, index) => {
      const highlight = isHighlight(step);
      return <motion.div key={step} initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5,
        delay: index * 0.1
      }} className="flex items-center gap-4" data-id="element-1186">
            <div className={`py-4 px-7 border ${highlight ? 'border-indigo/30 bg-indigo/5' : 'border-[#E4E6EC] bg-white'}`} data-id="element-1187">
              <span className={`font-serif text-lg md:text-xl ${highlight ? 'text-indigo' : 'text-[#121212]'}`} data-id="element-1188">
                {t(step)}
              </span>
            </div>
            {index < steps.length - 1 && <span className="text-[#121212]/25 text-xl" aria-hidden="true" data-id="element-1189">
                →
              </span>}
          </motion.div>;
    })}
    </div>;
}
function MetricCards({
  t
}: {
  t: (en: string) => string;
}) {
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-1190">
      {METRICS.map((m, index) => <motion.div key={m.label} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-60px'
    }} transition={{
      duration: 0.5,
      delay: index * 0.08
    }} className="border border-[#E4E6EC] p-8 text-center bg-white" data-id="element-1191">
          <div className="font-serif text-3xl md:text-4xl text-[#121212] mb-2" data-id="element-1192">
            {t(m.value)}
          </div>
          <div className="font-sans text-sm text-[#121212]/55" data-id="element-1193">
            {t(m.label)}
          </div>
        </motion.div>)}
    </div>;
}
export function Brossard() {
  const {
    lang
  } = useLanguage();
  const t = (en: string) => lang === 'FR' ? FR[en] ?? en : en;
  useEffect(() => {
    track('Case Study Viewed', {
      page: 'City of Brossard'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-1194">
      <Header data-id="element-1195" />

      <main className="pt-[120px]" data-id="element-1196">
        {/* SECTION 1 — HERO (Melbourne vs Brossard) */}
        <section className="pt-24 pb-32 px-6" data-id="element-1197">
          <div className="max-w-6xl mx-auto" data-id="element-1198">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="block text-center font-sans text-xs font-semibold tracking-[0.25em] text-indigo mb-16" data-id="element-1199">
              {lang === 'FR' ? 'ÉTUDE DE CAS' : 'CASE STUDY'}
            </motion.span>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-start mb-20" data-id="element-1200">
              {/* Left: Melbourne */}
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.8,
              delay: 0.1
            }} className="text-center md:text-right md:border-r border-[#E4E6EC] md:pr-24" data-id="element-1201">
                <div className="font-sans text-sm font-semibold tracking-widest text-[#121212]/40 mb-6" data-id="element-1202">
                  MELBOURNE
                </div>
                <div className="font-serif text-4xl text-[#121212]/40 mb-4" data-id="element-1203">
                  {lang === 'FR' ? '~10\u00A0000 capteurs' : '~10,000 Sensors'}
                </div>
                <div className="font-sans text-lg text-[#121212]/40 mb-10" data-id="element-1204">
                  {lang === 'FR' ? 'Mesure la ville' : 'Measures the city'}
                </div>
                <h1 className="font-serif text-4xl md:text-5xl text-[#121212]/40 leading-tight mb-6" data-id="element-1205">
                  {lang === 'FR' ? 'Melbourne a câblé ses rues.' : 'Melbourne wired its streets.'}
                </h1>
                <p className="font-sans text-xl text-[#121212]/40" data-id="element-1206">
                  {lang === 'FR' ? 'Les capteurs mesurent une ville.' : 'Sensors measure a city.'}
                </p>
              </motion.div>

              {/* Right: Brossard */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} className="text-center md:text-left" data-id="element-1207">
                <div className="font-sans text-sm font-semibold tracking-widest text-indigo mb-6" data-id="element-1208">
                  BROSSARD
                </div>
                <div className="font-serif text-4xl text-indigo mb-4" data-id="element-1209">
                  {lang === 'FR' ? '88\u00A0266 identités vérifiées' : '88,266 Verified Identities'}
                </div>
                <div className="font-sans text-lg text-indigo mb-10" data-id="element-1210">
                  {lang === 'FR' ? 'Agit pour les résidents' : 'Acts for residents'}
                </div>
                <h1 className="font-serif text-4xl md:text-5xl text-[#121212] leading-tight mb-6" data-id="element-1211">
                  {lang === 'FR' ? 'Brossard a connecté ses résidents.' : 'Brossard connected its residents.'}
                </h1>
                <p className="font-sans text-xl text-[#121212]/80" data-id="element-1212">
                  {lang === 'FR' ? "L'identité permet à l'IA d'agir." : 'Identity allows AI to act.'}
                </p>
              </motion.div>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }} className="flex flex-col items-center justify-center text-center" data-id="element-1213">
              <div className="flex flex-wrap items-center justify-center gap-4 mb-6" data-id="element-1214">
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Hero Primary'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium hover:bg-indigo/90 transition-colors" data-id="element-1215">
                  {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1216" />
                </Link>
                <a href="#proven" className="inline-flex items-center gap-2 px-8 py-4 border border-transparent text-[#121212]/60 text-base font-medium hover:text-[#121212] transition-colors" data-id="element-1217">
                  {lang === 'FR' ? 'Continuer ↓' : 'Continue ↓'}
                </a>
              </div>
              <p className="font-sans text-xs text-[#121212]/40" data-id="element-1218">
                {lang === 'FR' ? 'Nombre de capteurs de Melbourne\u00A0:' : 'Melbourne sensor count:'}{' '}
                <a href="https://www.lapresse.ca/international/asie-et-oceanie/la-presse-en-australie/la-presse-en-australie/un-grand-labo-pour-mieux-vivre-en-ville/2026-05-30/melbourne/la-ville-en-ebullition-aux-10-000-capteurs.php" target="_blank" rel="noopener noreferrer" className="text-[#4F46E5] underline underline-offset-2 hover:text-[#4F46E5]/80 transition-colors" data-id="element-1219">
                  {lang === 'FR' ? <>
                      La Presse, &ldquo;La ville en ébullition aux 10 000
                      capteurs,&rdquo; mai 2026.
                    </> : <>
                      La Presse, &ldquo;La ville en ébullition aux 10 000
                      capteurs,&rdquo; May 2026.
                    </>}
                </a>
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2 — PROVEN AT SCALE */}
        <section id="proven" className="py-28 px-6 bg-[#FAFAFB] border-t border-[#E4E6EC]" data-id="element-1220">
          <div className="max-w-4xl mx-auto" data-id="element-1221">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl font-normal text-[#121212] text-center mb-4" data-id="element-1222">
              {lang === 'FR' ? 'Éprouvé à grande échelle' : 'Proven at Scale'}
            </motion.h2>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-lg text-[#121212]/60 text-center max-w-2xl mx-auto mb-16" data-id="element-1223">
              {lang === 'FR' ? "L'un des plus hauts taux d'adoption de l'identité numérique au Canada." : 'One of the highest digital identity adoption rates in Canada.'}
            </motion.p>

            {/* Result — the outcome that matters */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.12
          }} className="max-w-2xl mx-auto mb-16 text-center" data-id="element-1224">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] uppercase text-indigo mb-6" data-id="element-1225">
                {lang === 'FR' ? 'Résultat' : 'Result'}
              </div>
              <div className="border border-[#E4E6EC] bg-white p-10 md:p-12" data-id="element-1226">
                <div className="font-serif text-5xl md:text-6xl text-[#121212] leading-none mb-5" data-id="element-1227">
                  400 <span className="text-indigo" data-id="element-1228">→</span> 5
                </div>
                <div className="font-sans text-sm text-[#121212]/60 leading-relaxed max-w-sm mx-auto" data-id="element-1229">
                  {lang === 'FR' ? 'Personnes en attente au comptoir pour un renouvellement de carte numérique — file quasi éliminée.' : 'People waiting in line for a digital card renewal — queue nearly eliminated.'}
                </div>
              </div>
            </motion.div>

            <MetricCards t={t} data-id="element-1230" />
          </div>

          {/* Production proof — BCITI live in Brossard */}
          <motion.div {...reveal} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="mt-16" data-id="element-1231">
            <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start justify-center gap-8 md:gap-10" data-id="element-1232">
              <div className="w-full max-w-[300px] mx-auto md:mx-0 md:flex-shrink-0" data-id="element-1233">
                <div className="flex items-center gap-2 mb-5 justify-center" data-id="element-1234">
                  <span className="inline-block w-3.5 h-3.5 bg-indigo" aria-hidden="true" data-id="element-1235" />
                  <span className="font-sans text-xs font-semibold tracking-[0.18em] uppercase text-[#121212]/55" data-id="element-1236">
                    {lang === 'FR' ? 'Carte citoyenne BCITI · Carte numérique citoyenne' : 'Carte citoyenne BCITI · Digital Citizen Card'}
                  </span>
                </div>
                <img src={lang === 'FR' ? 'https://cdn.magicpatterns.com/uploads/6ssDHDMHuDJ5EX6wAkS5z5/Capture_d’ecran,_le_2026-06-22_a_08.42.58.png' : 'https://cdn.magicpatterns.com/uploads/61CQZBUW2QoAMKbCeoDUkk/pasted-image.png'} alt={lang === 'FR' ? 'Écran de la carte citoyenne numérique bciti+ en production à Brossard' : 'bciti+ digital citizen card screen live in Brossard'} className="w-full h-auto border border-[#E4E6EC] bg-white" data-id="element-1237" />
              </div>

              <div className="w-full md:flex-1 md:min-w-0" data-id="element-1238">
                <div className="flex items-center gap-2 mb-5 justify-center md:justify-start" data-id="element-1239">
                  <span className="inline-block w-3.5 h-3.5 bg-indigo" aria-hidden="true" data-id="element-1240" />
                  <span className="font-sans text-xs font-semibold tracking-[0.18em] uppercase text-[#121212]/55" data-id="element-1241">
                    {lang === 'FR' ? 'Tableau de bord BCITI · Analytique des requêtes' : 'Tableau de bord BCITI · Requêtes Analytics'}
                  </span>
                </div>
                <img src="https://cdn.magicpatterns.com/uploads/bRyXP6hkH7M9i1qxoHZ5jj/Capture_d’ecran,_le_2026-06-22_a_08.50.11.png" alt="Tableau de bord BCITI à Brossard" className="w-full h-auto border border-[#E4E6EC] bg-white" data-id="element-1242" />
              </div>
            </div>
          </motion.div>
        </section>

        {/* SECTION 3 — WHY BROSSARD IS DIFFERENT */}
        <section className="py-28 px-6 border-t border-[#E4E6EC]" data-id="element-1243">
          <div className="max-w-4xl mx-auto" data-id="element-1244">
            <motion.span {...reveal} className="block text-center font-sans text-xs font-semibold tracking-[0.25em] text-indigo mb-6" data-id="element-1245">
              {lang === 'FR' ? 'POURQUOI BROSSARD EST DIFFÉRENTE' : 'WHY BROSSARD IS DIFFERENT'}
            </motion.span>
            <motion.h2 {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="font-serif text-4xl md:text-5xl font-normal text-[#121212] mb-20 text-center leading-tight" data-id="element-1246">
              {lang === 'FR' ? "L'intelligence commence par l'identité" : 'Intelligence Starts With Identity'}
            </motion.h2>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="grid grid-cols-1 md:grid-cols-2 border-t border-[#E4E6EC] mb-20" data-id="element-1247">
              <div className="py-10 md:pr-12 md:border-r border-[#E4E6EC]" data-id="element-1248">
                <div className="font-sans text-sm font-medium text-[#121212]/45 mb-8" data-id="element-1249">
                  {lang === 'FR' ? 'Ville intelligente traditionnelle' : 'Traditional Smart City'}
                </div>
                <ul className="space-y-4" data-id="element-1250">
                  {COMPARISON.traditional.map(item => <li key={item} className="font-serif text-2xl text-[#121212]/40" data-id="element-1251">
                      {t(item)}
                    </li>)}
                </ul>
              </div>
              <div className="py-10 md:pl-12" data-id="element-1252">
                <div className="font-sans text-sm font-medium text-indigo mb-8" data-id="element-1253">
                  {lang === 'FR' ? 'Modèle BCITI + ATNA' : 'BCITI + ATNA Model'}
                </div>
                <ul className="space-y-4" data-id="element-1254">
                  {COMPARISON.bciti.map((item, i, arr) => <li key={item} className={`font-serif text-2xl ${i === arr.length - 1 ? 'text-indigo' : 'text-[#121212]'}`} data-id="element-1255">
                      {t(item)}
                    </li>)}
                </ul>
              </div>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="text-center" data-id="element-1256">
              <div className="font-serif text-3xl md:text-4xl text-[#121212] mb-10 leading-tight" data-id="element-1257">
                <span className="text-[#121212]/40" data-id="element-1258">
                  {lang === 'FR' ? 'Un capteur mesure.' : 'A sensor measures.'}
                </span>
                <br data-id="element-1259" />
                {lang === 'FR' ? 'Une identité agit.' : 'An identity acts.'}
              </div>
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Why Brossard Different'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium hover:bg-indigo/90 transition-colors" data-id="element-1260">
                {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1261" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* SECTION 4 — TRUST BEFORE INTELLIGENCE */}
        <section className="py-28 px-6 bg-[#FAFAFB] border-t border-[#E4E6EC]" data-id="element-1262">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1263">
            <motion.div {...reveal} className="max-w-2xl mx-auto mb-20 space-y-4" data-id="element-1264">
              <p className="font-sans text-2xl text-[#121212]/60" data-id="element-1265">
                {lang === 'FR' ? "Brossard n'a pas commencé par l'IA." : 'Brossard did not start with AI.'}
              </p>
              <p className="font-sans text-2xl text-[#121212]/60" data-id="element-1266">
                {lang === 'FR' ? 'Elle a commencé par l\u2019identité, les communications et les services.' : 'It started with identity, communications, and services.'}
              </p>
              <p className="font-serif text-4xl text-[#121212] mt-8" data-id="element-1267">
                {lang === 'FR' ? "Cette fondation propulse aujourd'hui ATNA." : 'That foundation now powers ATNA.'}
              </p>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-1268">
              <HorizontalFlow steps={FOUNDATION_FLOW} t={t} data-id="element-1269" />
            </motion.div>
          </div>
        </section>

        {/* SECTION 5 — MEET ATNA */}
        <AtnaAssistant vertical={ATNA_CONFIG['brossard']} page="/brossard" data-id="element-1270" />

        {/* SECTION 6 — FINAL CTA */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1271">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1272">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl font-normal text-[#121212] mb-12 leading-tight" data-id="element-1273">
              {lang === 'FR' ? <>
                  Déployez la fondation.
                  <br data-id="element-1274" />
                  Mesurez le premier résultat.
                </> : <>
                  Deploy the foundation.
                  <br data-id="element-1275" />
                  Measure the first outcome.
                </>}
            </motion.h2>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="flex flex-wrap items-center justify-center gap-4 mb-8" data-id="element-1276">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Final CTA'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium hover:bg-indigo/90 transition-colors" data-id="element-1277">
                {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1278" />
              </Link>
              <Link to="/" className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium hover:border-[#121212] transition-colors" data-id="element-1279">
                {lang === 'FR' ? 'Retour à la plateforme' : 'Back to Platform'}
              </Link>
            </motion.div>

            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="font-sans text-sm text-[#121212]/50" data-id="element-1280">
              {lang === 'FR' ? 'Au service des communautés depuis 2017.' : 'Trusted by communities since 2017.'}
            </motion.p>
          </div>
        </section>
      </main>

      <Footer data-id="element-1281" />
    </div>;
}